import { useState } from "react";
import { formatMoney, toDecimal, toMinorUnits } from "@tovi/shared";
import { Button, Modal, useTranslate } from "@tovi/ui";
import type { Cart } from "@/lib/cart.ts";

/**
 * A whole-bill discount (`08-pos-terminal-and-qr.md` § A § Settlement).
 *
 * The amount is typed once and the **server** prorates it across the lines
 * that accept one — nothing here computes a new total. `cart.discount()` queues
 * it as a money operation, so the reduced figure the cashier reads and the
 * figure the customer is charged are the same number even when the terminal
 * spent the whole service offline.
 *
 * A discount larger than the bill is refused here as well as by the server,
 * whose answer is a 409 that would otherwise arrive minutes later out of the
 * sync badge — long after the cashier stopped looking.
 *
 * **Built from the spec text.** Without a capture of the till, this is a modal
 * with one amount field rather than a keypad panel, matching `SettleDialog`
 * because a cashier who has just learned one amount entry should not have to
 * learn a second. The reason to discount — a manager's comp, a promo taken off
 * a campaign — is not collected: `promoId` and `reason` go as null, and the
 * promo offer flow (`GET /orders/{id}/promos`) is a surface of its own.
 */
export function DiscountDialog({ cart, onClose }: { cart: Cart; onClose: () => void }) {
  const t = useTranslate();
  // The field is decimal and the book holds minor units, so an existing
  // discount is converted in rather than pasted in — `500` is five dollars
  // here and five hundred to `toMinorUnits`.
  const [typed, setTyped] = useState(
    cart.discountAmount > 0n ? toDecimal(cart.discountAmount.toString(), cart.currency) : "",
  );

  // `priced.subtotal` already has any current discount prorated out of it, and
  // the server checks against the order's undiscounted total. Comparing to the
  // reduced figure would refuse a raise the server would have taken — so the
  // discount is added back, which is the same number the server holds.
  const billTotal = (cart.priced?.subtotal.amount ?? 0n) + cart.discountAmount;
  const typedAmount = typed.trim() === "" ? 0n : BigInt(toMinorUnits(typed.trim(), cart.currency));
  const tooLarge = typedAmount > billTotal;

  function apply(amount: bigint): void {
    // Anything the server has not been told about goes first, exactly as
    // `SettleDialog` does before a payment: the discount is priced against the
    // order's own total, so one that arrived before its lines would be refused
    // for exceeding a bill the server still thinks is empty. `BillBookStore`
    // serialises its mutations, so calling these in order queues them in it.
    cart.fire();
    cart.discount(amount);
    onClose();
  }

  return (
    <Modal open title={t("pos.discount.title")} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-ink-muted">{t("pos.cart.subtotal")}</span>
          <span className="text-2xl font-semibold text-ink-body">
            {formatMoney(billTotal.toString(), cart.currency)}
          </span>
        </div>

        <label className="flex flex-col gap-1 text-sm text-ink-muted">
          {t("pos.discount.amount")}
          <input
            inputMode="decimal"
            className="h-12 rounded-control border border-line bg-surface px-3 text-lg text-ink-body"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={formatMoney("0", cart.currency)}
          />
        </label>

        {tooLarge ? <p className="text-sm text-danger">{t("pos.discount.tooLarge")}</p> : null}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("pos.discount.back")}
          </Button>
          {cart.discountAmount > 0n ? (
            <Button variant="secondary" onClick={() => apply(0n)}>
              {t("pos.discount.clear")}
            </Button>
          ) : null}
          <Button disabled={tooLarge || typedAmount <= 0n} onClick={() => apply(typedAmount)}>
            {t("pos.discount.apply")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
